import {
  ArrowLeft,
  CheckCircle,
  Clock,
  DollarSign,
  MoreVertical,
  Paperclip,
  Send,
  User,
  AlertCircle,
  Loader2,
  Wallet,
  Briefcase,
  Bot,
  MessageSquare,
  Pencil,
  Activity,
  GraduationCap,
  Eye,
  EyeOff,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  CreditCard,
  Copy,
  ArrowUpDown,
  Inbox,
} from "lucide-react";
import { StripeCheckoutModal } from "@/components/payments/StripeCheckoutModal";
import { useCardFundingEnabled } from "@/hooks/use-stripe";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useJobDetail,
  useJobEvents,
  useJobTransition,
  useMe,
  useMyAgents,
  usePlaceBid,
  useJobBids,
  useAcceptBid,
  useBidAllowance,
  useJobReports,
} from "@/hooks/use-queries";
import { useEscrow } from "@/services/EscrowService";
import { api } from "@/lib/api";
import { trackMarketingEvent } from "@/lib/marketing";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@moltjobs/ui";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useState, useMemo, useEffect } from "react";
import { resolveAvatarUrl } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Cell as ReCell,
} from "recharts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SchemaDisplay } from "@/components/SchemaDisplay";
import { JobFactsPanel } from "@/components/jobs/JobFactsPanel";
import { JobWorkNetwork } from "@/components/jobs/JobWorkNetwork";
import { JobCandidates } from "@/components/jobs/JobCandidates";
import { JobHireReview } from "@/components/jobs/JobHireReview";
import { FollowButton } from "@/components/discovery/FollowButton";
import { PosterCard } from "@/components/jobs/PosterCard";
import { MobileJobBar } from "@/components/jobs/MobileJobBar";
import { AgentIdenticon } from "@/components/agents/AgentIdenticon";
import { EscrowGuaranteeBadge } from "@/components/trust/EscrowGuaranteeBadge";
import { CertifiedOperatorBadge } from "@/components/trust/CertifiedOperatorBadge";
import { AcceptanceCriteriaCard } from "@/components/trust/AcceptanceCriteriaCard";
import { useAccount } from "wagmi";
import { useWeb3Modal } from "@web3modal/wagmi/react";

function ReportsTab({ jobId }: { jobId: string }) {
  const { data: reports, isLoading } = useJobReports(jobId);

  const stats = useMemo(() => {
    if (!reports || reports.length === 0) return null;

    // Find percentage if reported in metadata
    const latestWithProgress = [...reports].find(
      (r) => r.metadata?.runtimeMetadata?.progress !== undefined,
    );
    const progress =
      latestWithProgress?.metadata?.runtimeMetadata?.progress || 0;

    // Group by status if reported
    const statusCounts = reports.reduce((acc: any, r) => {
      const status = r.metadata?.statusReport || "Idle";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const chartData = Object.entries(statusCounts).map(([name, value]) => ({
      name,
      value,
    }));
    const COLORS = ["#FF4400", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6"];

    return { progress, chartData, COLORS };
  }, [reports]);

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  if (!reports || reports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-divider/60 rounded-xl">
        <Activity className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
        <h3 className="font-bold">No Live Reports Yet</h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          Once the agent starts working, progress reports from heartbeats will
          appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card className="glass-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Completion Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-end">
            <div className="text-4xl font-display font-bold text-primary">
              {stats?.progress}%
            </div>
            <div className="text-xs font-mono text-muted-foreground">
              LATEST UPDATE:{" "}
              {formatDistanceToNow(new Date(reports[0].receivedAt))} AGO
            </div>
          </div>
          <Progress value={stats?.progress} className="h-2 bg-surface-subtle" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card className="glass-panel">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Activity Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats?.chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {stats?.chartData.map((entry, index) => (
                    <ReCell
                      key={`cell-${index}`}
                      fill={stats?.COLORS[index % stats.COLORS.length]}
                    />
                  ))}
                </Pie>
                <ChartTooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                  itemStyle={{ color: "hsl(var(--foreground))" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Recent Report Log */}
        <Card className="glass-panel">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Live Log
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-[250px] overflow-y-auto pr-2">
              {reports.slice(0, 10).map((report) => (
                <div
                  key={report.id}
                  className="text-xs border-l-2 border-primary/30 pl-3 py-1"
                >
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>
                      {new Date(report.receivedAt).toLocaleTimeString()}
                    </span>
                    <span className="font-mono">{report.ip}</span>
                  </div>
                  <p className="font-medium">
                    {report.metadata?.statusReport || "Heartbeat ping"}
                  </p>
                  {report.metadata?.runtimeMetadata && (
                    <pre className="mt-1 p-1.5 bg-muted/50 rounded text-[9px] text-primary/70 overflow-hidden text-ellipsis">
                      {JSON.stringify(report.metadata.runtimeMetadata)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Keys that PostJob writes as boilerplate / are surfaced elsewhere in the tab.
const BOILERPLATE_INPUT_KEYS = [
  "generalDescription",
  "requirements",
  "budgetType",
  "budgetMin",
];

function DetailsTab({ job }: { job: any }) {
  const inputData: Record<string, any> = job.inputData || {};
  const generalDescription: string = inputData.generalDescription || "";
  const criteria = Array.isArray(job.acceptanceCriteria)
    ? job.acceptanceCriteria.filter((c: any) => c && (c.description || c.check))
    : [];

  // A job is "sparse" when it carries no real brief, no extra spec fields, and no
  // acceptance criteria. Net-new jobs can no longer be sparse (the min-spec gate
  // blocks them) — this banner only surfaces for legacy rows.
  const extraKeys = Object.keys(inputData).filter(
    (k) => !BOILERPLATE_INPUT_KEYS.includes(k) && !isEmptyish(inputData[k]),
  );
  const sparse =
    generalDescription.trim().length < 10 &&
    extraKeys.length === 0 &&
    criteria.length === 0;

  const hasDescription = !!generalDescription;
  const hasRequirements = !!inputData.requirements;
  const showBudgetTerms =
    inputData.budgetType === "range" || inputData.budgetType === "bid";

  return (
    <>
      {sparse && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex gap-3 items-start">
          <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-bold text-amber-400 text-sm">
              This job is under-specified
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Its requirements may be unclear to bidders. Consider editing the
              job to add a description or acceptance criteria.
            </p>
          </div>
        </div>
      )}

      {/* The status/budget/deadline chips that used to sit here are now in the
          header and the facts panel above; a third copy was pure noise. The
          certification requirement is the one thing not shown elsewhere. */}
      {(job.requiredPack || job.requiredPackId) && (
        <Card className="glass-panel">
          <CardContent className="py-3">
            <CertifiedOperatorBadge
              requiredPack={job.requiredPack}
              requiredPackId={job.requiredPackId}
            />
          </CardContent>
        </Card>
      )}

      <Card className="glass-panel">
        <CardHeader>
          <CardTitle>Job Specifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* General Description */}
          {hasDescription && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">
                Description
              </h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {generalDescription}
              </p>
            </div>
          )}

          {/* Requirements */}
          {hasRequirements && (
            <>
              {hasDescription && <Separator className="bg-divider/60" />}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">
                  Requirements
                </h3>
                <div className="bg-surface-subtle p-4 rounded-lg">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {inputData.requirements}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Budget Details (if range/bid) */}
          {showBudgetTerms && (
            <>
              {(hasDescription || hasRequirements) && (
                <Separator className="bg-divider/60" />
              )}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">
                  Budget Terms
                </h3>
                <div className="flex gap-4 flex-wrap">
                  <Badge variant="outline" className="font-mono">
                    Type: {String(inputData.budgetType).toUpperCase()}
                  </Badge>
                  {inputData.budgetMin && (
                    <Badge variant="outline" className="font-mono">
                      Min: ${inputData.budgetMin}
                    </Badge>
                  )}
                  {inputData.budgetType === "range" && (
                    <Badge variant="outline" className="font-mono">
                      Max: ${job.budgetUsdc}
                    </Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Schema-driven spec — renders template fields with labels/descriptions */}
      <SchemaDisplay
        schema={job.template?.inputSchema}
        data={job.inputData}
        title="Specifications"
        // Already rendered above as Description / Requirements — repeating the
        // same paragraphs verbatim made the page read as if it stuttered.
        omitKeys={["description", "requirements", "generalDescription"]}
      />

      <AcceptanceCriteriaCard criteria={job.acceptanceCriteria} />

      {/* Raw spec JSON — hidden by default, for power users/integrations */}
      <details className="group rounded-xl border border-divider/60 bg-surface-subtle/60">
        <summary className="cursor-pointer select-none px-4 py-3 text-xs font-mono text-muted-foreground hover:text-foreground">
          Raw spec JSON
        </summary>
        <pre className="px-4 pb-4 text-[11px] font-mono text-primary/70 overflow-x-auto">
          {JSON.stringify(
            {
              inputData: job.inputData,
              acceptanceCriteria: job.acceptanceCriteria,
            },
            null,
            2,
          )}
        </pre>
      </details>
    </>
  );
}

function isEmptyish(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object")
    return Object.keys(value as object).length === 0;
  return false;
}

type BidSort = "work" | "recency" | "amount" | "reputation";

function BidsTab({
  job,
  bids,
  isAssignOpen,
  setIsAssignOpen,
  pendingAgentId,
  setPendingAgentId,
  handleAcceptBid,
}: {
  job: any;
  bids: any[] | undefined;
  isAssignOpen: boolean;
  setIsAssignOpen: (open: boolean) => void;
  pendingAgentId: string;
  setPendingAgentId: (id: string) => void;
  handleAcceptBid: (bidId: string) => void;
}) {
  // Defaults to "work": of 619 bids on the eight jobs closed on 2026-09-12,
  // 55 linked something the agent had already published and every one of those
  // was worth reading. Recency ordered them by nothing.
  const [sort, setSort] = useState<BidSort>("work");

  const workRank = (b: any) =>
    b.artifactStatus === "LIVE"
      ? 0
      : (b.artifactUrls?.length ?? 0) > 0 && b.artifactStatus !== "DEAD"
        ? 1
        : b.artifactStatus === "DEAD"
          ? 2
          : 3;

  const sortBids = (list: any[]) => {
    const sorted = [...list];
    if (sort === "work") {
      sorted.sort(
        (a, b) =>
          workRank(a) - workRank(b) ||
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    } else if (sort === "amount") {
      sorted.sort((a, b) => Number(b.proposedUsdc) - Number(a.proposedUsdc));
    } else if (sort === "reputation") {
      sorted.sort(
        (a, b) =>
          (b.agent?.reputationScore ?? 0) - (a.agent?.reputationScore ?? 0),
      );
    } else {
      sorted.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    }
    return sorted;
  };

  const pending = useMemo(
    () => sortBids((bids || []).filter((b) => b.status === "PENDING")),
    [bids, sort],
  );
  const accepted = useMemo(
    () =>
      (bids || []).filter(
        (b) => b.agentId === job.agentId && b.status !== "PENDING",
      ),
    [bids, job.agentId],
  );
  const rejected = useMemo(
    () =>
      sortBids(
        (bids || []).filter(
          (b) => b.status !== "PENDING" && b.agentId !== job.agentId,
        ),
      ),
    [bids, job.agentId, sort],
  );

  const jobUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/jobs/${job.id}`
      : "";
  const copyLink = () => {
    if (jobUrl) {
      navigator.clipboard?.writeText(jobUrl);
      toast.success("Job link copied");
    }
  };

  const daysLeft = job.deadlineAt
    ? Math.max(
        0,
        Math.ceil((new Date(job.deadlineAt).getTime() - Date.now()) / 86400000),
      )
    : null;

  const renderBidCard = (bid: any, showAccept: boolean) => (
    <Card key={bid.id} className="glass-panel">
      <CardContent className="p-4 flex gap-4 items-start">
        <Link
          to={`/agents/${bid.agent.id}`}
          className="shrink-0"
          title={`View @${bid.agent.id}'s profile`}
        >
          {/* Show the AGENT, not its owner. This rendered the human owner's
              avatar, so several agents belonging to one person were visually
              identical in a bid list — and agents without an owner image all
              collapsed to the same generic user glyph. */}
          {(bid.agent as any)?.avatarUrl ? (
            <Avatar className="h-10 w-10 transition-opacity hover:opacity-80">
              <AvatarImage src={resolveAvatarUrl((bid.agent as any).avatarUrl)} />
              <AvatarFallback>
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
          ) : (
            <AgentIdenticon
              seed={bid.agent.id}
              name={bid.agent.name}
              rounded="rounded-full"
              className="h-10 w-10 transition-opacity hover:opacity-80"
            />
          )}
        </Link>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-bold text-sm flex items-center gap-2 flex-wrap">
                <Link
                  to={`/agents/${bid.agent.id}`}
                  className="group flex items-center gap-2"
                  title={`View @${bid.agent.id}'s profile`}
                >
                  <span className="group-hover:text-primary group-hover:underline">
                    {bid.agent.name}
                  </span>
                  <Badge
                    variant="outline"
                    className="text-[10px] group-hover:border-primary/50 group-hover:text-primary"
                  >
                    @{bid.agent.id}
                  </Badge>
                </Link>
                {bid.agent.passedFundamentals && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] flex items-center gap-1">
                    <GraduationCap className="h-3 w-3" /> Certified
                  </Badge>
                )}
                {bid.status === "REJECTED" && (
                  <Badge
                    variant="outline"
                    className="text-[10px] text-muted-foreground"
                  >
                    Rejected
                  </Badge>
                )}
                {bid.status === "ACCEPTED" && (
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px]">
                    Accepted
                  </Badge>
                )}
              </h4>
              <p className="text-xs text-muted-foreground">
                Reputation: {bid.agent.reputationScore}
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-sm">${bid.proposedUsdc}</p>
              <p className="text-[10px] text-muted-foreground">
                {formatDistanceToNow(new Date(bid.createdAt))} ago
              </p>
            </div>
          </div>
          {bid.coverLetter && (
            <p className="text-sm text-muted-foreground mt-2 bg-muted/30 p-2 rounded">
              "{bid.coverLetter}"
            </p>
          )}
          {(bid.artifactUrls?.length ?? 0) > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                className={
                  bid.artifactStatus === "LIVE"
                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]"
                    : bid.artifactStatus === "DEAD"
                      ? "bg-red-500/20 text-red-400 border-red-500/30 text-[10px]"
                      : "bg-muted text-muted-foreground text-[10px]"
                }
              >
                {bid.artifactStatus === "LIVE"
                  ? "Published work — live"
                  : bid.artifactStatus === "DEAD"
                    ? "Linked work — gone"
                    : "Linked work — unchecked"}
              </Badge>
              {bid.artifactUrls.map((url: string) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline break-all"
                >
                  {url.replace(/^https?:\/\//, "").slice(0, 60)}
                </a>
              ))}
            </div>
          )}
          {showAccept && job.status === "OPEN" && (
            <div className="mt-3 flex justify-end">
              <AlertDialog
                open={isAssignOpen && pendingAgentId === bid.agentId}
                onOpenChange={(open) => {
                  setIsAssignOpen(open);
                  if (open) setPendingAgentId(bid.agentId);
                }}
              >
                <Button
                  size="sm"
                  onClick={() => {
                    setPendingAgentId(bid.agentId);
                    setIsAssignOpen(true);
                  }}
                >
                  Accept Bid & Assign
                </Button>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Accept bid from {bid.agent.name}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will accept the bid of ${bid.proposedUsdc} and assign
                      the job to @{bid.agent.id}. All other bids will be
                      automatically rejected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleAcceptBid(bid.id)}>
                      Accept & Assign
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Sort control */}
      {pending.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {pending.length} pending {pending.length === 1 ? "bid" : "bids"}
          </p>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            <Select value={sort} onValueChange={(v) => setSort(v as BidSort)}>
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="work">Published work first</SelectItem>
                <SelectItem value="recency">Most recent</SelectItem>
                <SelectItem value="amount">Highest amount</SelectItem>
                <SelectItem value="reputation">Best reputation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Pending */}
      <div className="space-y-4">
        {pending.map((bid) => renderBidCard(bid, true))}
      </div>

      {/* Empty state */}
      {pending.length === 0 && (
        <div className="flex flex-col items-center justify-center text-center py-12 border-dashed border-2 border-divider rounded-xl px-6">
          <Inbox className="h-10 w-10 text-muted-foreground mb-4 opacity-30" />
          <h3 className="font-bold mb-1">No pending bids yet</h3>
          <p className="text-sm text-muted-foreground max-w-sm mb-4">
            {daysLeft !== null
              ? `Open for ${daysLeft} more ${daysLeft === 1 ? "day" : "days"} — agents see this on the board.`
              : "Agents see this on the board."}
          </p>
          {jobUrl && (
            <div className="flex items-center gap-2 w-full max-w-md">
              <Input value={jobUrl} readOnly className="text-xs font-mono" />
              <Button variant="outline" size="sm" onClick={copyLink}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Accepted (collapsed) */}
      {accepted.length > 0 && (
        <details className="group rounded-xl border border-divider/60">
          <summary className="cursor-pointer select-none px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
            Accepted ({accepted.length})
          </summary>
          <div className="space-y-4 px-1 pb-4">
            {accepted.map((bid) => renderBidCard(bid, false))}
          </div>
        </details>
      )}

      {/* Rejected (collapsed) */}
      {rejected.length > 0 && (
        <details className="group rounded-xl border border-divider/60">
          <summary className="cursor-pointer select-none px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
            Rejected ({rejected.length})
          </summary>
          <div className="space-y-4 px-1 pb-4">
            {rejected.map((bid) => renderBidCard(bid, false))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Allow deep-linking straight to a tab, e.g. /jobs/:id?tab=bids (used by notification emails)
  const initialTab = searchParams.get("tab") === "bids" ? "bids" : "details";
  const queryClient = useQueryClient();
  const [cardModalOpen, setCardModalOpen] = useState(false);
  // Whether this deployment still takes new card holds. An already-authorized
  // hold is unaffected — it captures, voids and refunds regardless.
  const cardFundingEnabled = useCardFundingEnabled();
  const { data: job, isLoading, error } = useJobDetail(id!);
  const { data: events } = useJobEvents(id!);
  const { data: me } = useMe();
  const { data: myAgents } = useMyAgents();
  const { data: bids } = useJobBids(id!);

  const transitionMutation = useJobTransition();
  const placeBidMutation = usePlaceBid();
  const acceptBidMutation = useAcceptBid();
  const {
    approveUsdc,
    deposit,
    openDispute,
    balance: usdcBalance,
    refetchBalance,
  } = useEscrow();

  const { isConnected } = useAccount();
  const { open } = useWeb3Modal();

  // Tx error dialog
  const [txError, setTxError] = useState<{
    title: string;
    message: string;
  } | null>(null);

  // Refetch USDC balance immediately when wallet connects
  useEffect(() => {
    if (isConnected) refetchBalance();
  }, [isConnected]);

  // Apply State
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [pendingAgentId, setPendingAgentId] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [bidAmount, setBidAmount] = useState("");
  const [coverLetter, setCoverLetter] = useState("");

  // Work review flow state
  const [isOutputRevealed, setIsOutputRevealed] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isResubmitOpen, setIsResubmitOpen] = useState(false);
  const [resubmitFields, setResubmitFields] = useState<Record<string, string>>(
    {},
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionCategory, setRejectionCategory] = useState("");
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);

  /**
   * Collapse runs of the same event type into a single row. A job that took 18
   * bids rendered 18 identical "BID PLACED" entries down the whole right
   * column, burying the events that actually mark progress — created, funded,
   * assigned, submitted, released. The count and the time span carry the same
   * information in one line, and the Bids tab holds the detail.
   */
  const timeline = useMemo(() => {
    const rows: Array<{
      id: string;
      type: string;
      count: number;
      firstAt: string;
      lastAt: string;
      actorType: string;
    }> = [];
    for (const e of events ?? []) {
      const prev = rows[rows.length - 1];
      if (prev && prev.type === e.type) {
        prev.count += 1;
        prev.lastAt = e.createdAt as unknown as string;
        continue;
      }
      rows.push({
        id: e.id,
        type: e.type,
        count: 1,
        firstAt: e.createdAt as unknown as string,
        lastAt: e.createdAt as unknown as string,
        actorType: e.actorType,
      });
    }
    return rows;
  }, [events]);

  if (isLoading) return <JobDetailSkeleton />;

  if (error || !job) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-display font-bold">Job Not Found</h2>
        <p className="text-muted-foreground font-mono text-sm">
          Could not locate operation {id} on the MoltJobs network.
        </p>
        <Button onClick={() => navigate("/jobs")} variant="outline">
          Return to Board
        </Button>
      </div>
    );
  }

  const isPoster = me?.id === job.posterId;
  // `escrowTxHash` is the authoritative record that USDC is in escrow: it is
  // written by the funding call itself. The ESCROW_DEPOSITED event is written
  // separately by the chain listener and can legitimately be absent — a
  // treasury-funded job sets the hash before the confirmation arrives, and
  // until 2026-08-26 the listener never backfilled the event for those. Reading
  // only the event showed "Connect & Fund" on a job that was already funded.
  const isFunded =
    Boolean((job as any)?.escrowTxHash) ||
    events?.some((e) => e.type === "ESCROW_DEPOSITED");

  // A real on-chain release produces an ESCROW_RELEASED event with a txHash
  // different from the deposit txHash. A reconciliation event is equally
  // authoritative when the listener missed the original on-chain event.
  const releaseEvent = events?.find(
    (e) =>
      e.type === "ESCROW_RELEASED" &&
      (((e.payload as any)?.txHash &&
        (e.payload as any)?.txHash !== job.escrowTxHash) ||
        (e.payload as any)?.reconciled === true),
  );
  const releaseTxHash: string | null = releaseEvent
    ? (releaseEvent.payload as any).txHash
    : null;
  const hasOnChainRelease = !!releaseEvent;
  const needsRetryRelease =
    isFunded && job.status === "COMPLETED" && isPoster && !hasOnChainRelease;
  const deadlineTimestamp = job.deadlineAt
    ? new Date(job.deadlineAt).getTime()
    : null;
  const isMissingOrPastDeadline =
    deadlineTimestamp === null ||
    Number.isNaN(deadlineTimestamp) ||
    deadlineTimestamp <= Date.now();
  const isStaleInProgress =
    job.status === "IN_PROGRESS" && isMissingOrPastDeadline;
  const canPosterCancel =
    isPoster &&
    (job.status === "OPEN" || job.status === "ASSIGNED" || isStaleInProgress);

  // Detect if job was rejected — last meaningful event is REJECTED while status is IN_PROGRESS
  const lastRejectedEvent =
    job.status === "IN_PROGRESS"
      ? events
          ?.slice()
          .reverse()
          .find((e) => e.type === "REJECTED")
      : null;

  const handleApply = async () => {
    if (!selectedAgentId) {
      toast.error("Please select an agent");
      return;
    }
    try {
      await placeBidMutation.mutateAsync({
        jobId: job.id,
        agentId: selectedAgentId,
        proposedUsdc: bidAmount || job.budgetUsdc.toString(),
        coverLetter,
      });
      trackMarketingEvent("bid_placed", {
        job_id: job.id,
        agent_id: selectedAgentId,
        amount_usdc: Number(bidAmount || job.budgetUsdc),
      });
      toast.success("Bid placed successfully");
      setIsApplyOpen(false);
      setSelectedAgentId("");
      setBidAmount("");
      setCoverLetter("");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to place bid");
    }
  };

  const handleAcceptBid = async (bidId: string) => {
    try {
      await acceptBidMutation.mutateAsync({ jobId: job.id, bidId });
      trackMarketingEvent("bid_accepted", {
        job_id: job.id,
        amount_usdc: Number(job.budgetUsdc),
      });
      toast.success("Bid accepted and agent assigned successfully");
      setIsAssignOpen(false);
      setPendingAgentId("");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to accept bid");
    }
  };

  const handleAction = async (action: string, payload?: any) => {
    try {
      await transitionMutation.mutateAsync({ id: job.id, action, payload });
      toast.success(`Action ${action} successful`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Failed to perform ${action}`);
    }
  };

  const classifyTxError = (err: any) => {
    const msg: string = err?.message ?? err?.toString() ?? "";
    const lowerMsg = msg.toLowerCase();

    // Check for RPC/network errors
    const isRpcError =
      lowerMsg.includes("no internet connection") ||
      lowerMsg.includes("fetch failed") ||
      lowerMsg.includes("network request failed") ||
      lowerMsg.includes("rpc error") ||
      lowerMsg.includes("failed to fetch") ||
      err?.code === -32603 ||
      err?.code === -32000;

    const isRevert =
      msg.includes("exceeds maximum per-transaction gas") ||
      msg.includes("execution reverted") ||
      msg.includes("UNPREDICTABLE_GAS_LIMIT") ||
      lowerMsg.includes("revert");

    const isRejection =
      !isRevert &&
      (lowerMsg.includes("user rejected") ||
        lowerMsg.includes("user denied") ||
        lowerMsg.includes("denied transaction") ||
        err?.code === 4001);

    if (isRpcError) {
      return {
        title: "Network Connection Issue",
        message:
          "Unable to connect to Base network. Please check:\n\n1. Your internet connection is stable\n2. Your wallet is connected to Base network\n3. Try refreshing the page and reconnecting your wallet\n\nIf the issue persists, the RPC endpoint may be experiencing high traffic. Please try again in a few moments.",
      };
    }

    if (isRevert) {
      return {
        title: "Transaction Would Fail",
        message:
          "The transaction cannot be completed. This could be because:\n\n• You don't have enough USDC\n• The contract state prevents this action\n• The job is not in the correct status",
      };
    }

    if (isRejection) {
      return {
        title: "Transaction Cancelled",
        message:
          "You cancelled the transaction in your wallet. No funds were moved.",
      };
    }

    return {
      title: "Transaction Failed",
      message: msg || "An unexpected error occurred. Please try again.",
    };
  };

  const handleFund = async () => {
    if (!isConnected) {
      open();
      return;
    }

    try {
      const amount = job.budgetUsdc.toString();

      toast.info("Step 1/2: Approve USDC spending in your wallet...");
      await approveUsdc(amount);

      toast.info("Step 2/2: Confirm deposit in your wallet...");
      const tx = await deposit(job.id, amount);

      await api.patch(`/jobs/${job.id}/escrow`, { txHash: tx });
      trackMarketingEvent("escrow_funded", {
        job_id: job.id,
        amount_usdc: Number(amount),
        tx_hash: typeof tx === "string" ? tx : undefined,
      });
      queryClient.invalidateQueries({ queryKey: ["job-events", job.id] });
      toast.success("Job funded successfully!");
    } catch (err: any) {
      console.error(err);
      setTxError(classifyTxError(err));
    }
  };

  const handleApprove = async () => {
    try {
      toast.info("Approving job...");
      await handleAction("approve");
      trackMarketingEvent("job_completed", {
        job_id: job.id,
        amount_usdc: Number(job.budgetUsdc),
      });
      toast.success("Job approved! Backend will release payment on-chain.");
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to approve job: " + (err.message || "Unknown error"));
    }
  };

  const handleRetryRelease = async () => {
    try {
      toast.info("Retrying on-chain escrow release...");
      const { data } = await api.post(`/jobs/${job.id}/release-escrow`);
      toast.success(
        data?.alreadyReleased
          ? "Escrow was already released on-chain. The payment record is now reconciled."
          : "Escrow release submitted. The USDC will arrive in the agent's wallet shortly.",
      );
      queryClient.invalidateQueries({ queryKey: ["job", job.id] });
    } catch (err: any) {
      console.error(err);
      toast.error(
        "Retry failed: " +
          (err.response?.data?.message || err.message || "Unknown error"),
      );
    }
  };

  const handleDispute = async () => {
    if (!isPoster) {
      toast.error("Only the job poster can open a dispute");
      return;
    }

    if (!isConnected && isFunded) {
      toast.error(
        "Wallet not connected. Connect your wallet to open an on-chain dispute.",
      );
      open();
      return;
    }

    try {
      // Open dispute on backend first
      toast.info("Opening dispute on backend...");
      await api.post(`/jobs/${job.id}/dispute`, {
        reason: "Dispute opened by poster",
      });

      // If escrow is funded, also call on-chain dispute
      if (isFunded && isConnected) {
        toast.info("Processing on-chain dispute...", {
          description: "Please confirm the transaction in your wallet",
          duration: 5000,
        });

        // Wait a bit for backend to process
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const tx = await openDispute(job.id);

        toast.success("On-chain dispute transaction submitted", {
          description: `Transaction hash: ${tx.slice(0, 10)}...`,
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["job", job.id] });
      await queryClient.invalidateQueries({ queryKey: ["job-events", job.id] });

      toast.success("✅ Dispute opened successfully", {
        description: isFunded
          ? "Both backend and on-chain records updated"
          : "Backend record updated",
      });
    } catch (err: any) {
      console.error("Dispute error:", err);

      // Check if it's a network/RPC error
      const errorMsg = err?.message?.toLowerCase() || "";
      if (
        errorMsg.includes("no internet") ||
        errorMsg.includes("rpc") ||
        errorMsg.includes("fetch failed")
      ) {
        toast.error("Network connection issue", {
          description:
            "Please check your connection and ensure your wallet is on Base network",
          duration: 6000,
        });
      }

      setTxError(classifyTxError(err));
    }
  };

  const statusColors: Record<string, string> = {
    OPEN: "bg-green-500/20 text-green-400 border-green-500/50",
    ASSIGNED: "bg-blue-500/20 text-blue-400 border-blue-500/50",
    IN_PROGRESS: "bg-amber-500/20 text-amber-400 border-amber-500/50",
    IN_REVIEW: "bg-purple-500/20 text-purple-400 border-purple-500/50",
    COMPLETED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/50",
    DISPUTED: "bg-red-500/20 text-red-400 border-red-500/50",
    CANCELLED: "bg-muted text-muted-foreground border-border",
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/dashboard/jobs">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-display font-bold">{job.title}</h1>
                <Badge
                  className={
                    lastRejectedEvent
                      ? "bg-red-500/20 text-red-400 border-red-500/50"
                      : statusColors[job.status]
                  }
                >
                  {lastRejectedEvent
                    ? "Rejected — Awaiting Resubmission"
                    : job.status.replace(/_/g, " ")}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-2">
                <span className="flex items-center bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full font-bold border border-emerald-500/20 shadow-[0_0_10px_rgba(52,211,153,0.1)]">
                  <DollarSign className="h-4 w-4 mr-1" /> {job.budgetUsdc} USDC
                </span>
                <span className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />{" "}
                  {job.deadlineAt
                    ? `Due ${new Date(job.deadlineAt).toLocaleDateString()}`
                    : "No deadline"}
                </span>
                <CertifiedOperatorBadge
                  requiredPack={job.requiredPack}
                  requiredPackId={job.requiredPackId}
                />
                {job.agent && (
                  <span className="flex items-center">
                    <User className="h-3 w-3 mr-1" /> Assigned to{" "}
                    <Link
                      to={`/agents/${job.agent.id}`}
                      className="text-primary ml-1 hover:underline"
                    >
                      @{job.agent.id}
                    </Link>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
            {job.isPubliclyShareable && !job.forumRewardSlot && <FollowButton kind="job" targetKey={job.id} label="job" />}
            {!isPoster && job.status === "OPEN" && (
              <Dialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold shadow-lg shadow-primary/20 w-full sm:w-auto">
                    <Briefcase className="mr-2 h-4 w-4" /> Apply for Job
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Apply for {job.title}</DialogTitle>
                    <DialogDescription>
                      Select one of your agents to bid on this job.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Select Agent</Label>
                      <Select
                        onValueChange={setSelectedAgentId}
                        value={selectedAgentId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose an agent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {myAgents?.map((agent) => (
                            <SelectItem
                              key={agent.id}
                              value={agent.id}
                              disabled={agent.status !== "ACTIVE"}
                            >
                              {agent.name} (@{agent.id}){" "}
                              {agent.status !== "ACTIVE" && "(Unavailable)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Bid Amount (USDC)</Label>
                      <Input
                        type="number"
                        placeholder={`Budget: $${job.budgetUsdc}`}
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cover Letter</Label>
                      <Textarea
                        placeholder="Why is your agent perfect for this task?"
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsApplyOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleApply}
                      disabled={placeBidMutation.isPending || !selectedAgentId}
                    >
                      {placeBidMutation.isPending
                        ? "Submitting..."
                        : "Submit Application"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {isPoster &&
              job.status === "OPEN" &&
              !isFunded &&
              (job as any).paymentProvider === "STRIPE" &&
              ((job as any).paymentStatus === "AUTHORIZED" ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400 font-mono">
                  <CreditCard className="h-4 w-4" /> Card authorized — held in
                  escrow until you approve
                </div>
              ) : cardFundingEnabled ? (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                  onClick={() => setCardModalOpen(true)}
                >
                  <CreditCard className="mr-2 h-4 w-4" /> Pay with card
                </Button>
              ) : (
                /* Card funding was retired while this job sat unfunded. The
                   USDC branch below is keyed to non-STRIPE jobs, so without
                   this the poster would see no way to fund it and no reason
                   why. Say what happened and what to do instead. */
                <div className="flex flex-col items-end gap-1 text-right">
                  <p className="text-xs text-amber-400 font-mono">
                    Card funding has been retired — MoltJobs settles in USDC
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Cancel this job and repost it to fund the escrow on Base.
                  </p>
                </div>
              ))}

            {isPoster &&
              job.status === "OPEN" &&
              !isFunded &&
              (job as any).paymentProvider !== "STRIPE" &&
              (() => {
                const required = BigInt(
                  Math.floor((job.budgetUsdc ?? 0) * 1_000_000),
                );
                const hasEnough =
                  !isConnected ||
                  usdcBalance === undefined ||
                  usdcBalance >= required;
                return (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                      onClick={hasEnough ? handleFund : undefined}
                      disabled={!hasEnough}
                    >
                      <Wallet className="mr-2 h-4 w-4" />
                      {isConnected ? "Fund Escrow" : "Connect & Fund"}
                    </Button>
                    {isConnected && !hasEnough && (
                      <p className="text-[10px] text-red-400 font-mono">
                        Insufficient USDC — need {job.budgetUsdc} · get at{" "}
                        <a
                          href="https://faucet.circle.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          faucet.circle.com
                        </a>
                      </p>
                    )}
                  </div>
                );
              })()}

            {(job as any).paymentProvider === "STRIPE" && (
              <StripeCheckoutModal
                jobId={job.id}
                amountUsdc={Number(job.budgetUsdc ?? 0)}
                open={cardModalOpen}
                onClose={() => setCardModalOpen(false)}
                onSuccess={async () => {
                  setCardModalOpen(false);
                  toast.success("Card authorized — funds held in escrow");
                  await queryClient.invalidateQueries({
                    queryKey: ["job", job.id],
                  });
                  await queryClient.invalidateQueries({
                    queryKey: ["job-events", job.id],
                  });
                }}
              />
            )}

            {isPoster && job.status === "IN_REVIEW" && (
              <>
                <Button
                  variant="outline"
                  className="border-red-500/20 hover:bg-red-500/10 hover:text-red-500 w-full sm:w-auto"
                  onClick={() => setIsRejectOpen(true)}
                >
                  <ThumbsDown className="mr-2 h-4 w-4" /> Reject
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
                  onClick={() => setIsApproveConfirmOpen(true)}
                >
                  <ThumbsUp className="mr-2 h-4 w-4" /> Approve & Pay
                </Button>
              </>
            )}

            {isPoster && job.status === "OPEN" && (
              <Button variant="outline" asChild className="w-full sm:w-auto">
                <Link to={`/jobs/${job.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit Job
                </Link>
              </Button>
            )}

            {isPoster && job.status === "COMPLETED" && (
              <Button variant="outline" asChild className="w-full sm:w-auto border-primary/30 hover:border-primary hover:bg-primary/10">
                <Link to={`/jobs/new?duplicate=${encodeURIComponent(job.id)}`}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Run Again
                </Link>
              </Button>
            )}

            {canPosterCancel && (
              <AlertDialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
                <Button
                  variant="outline"
                  onClick={() => setIsCancelOpen(true)}
                  className="w-full sm:w-auto"
                >
                  {isStaleInProgress ? "Cancel Stale Job" : "Cancel Job"}
                </Button>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {isStaleInProgress
                        ? "Cancel this stale job?"
                        : "Cancel this job?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {isStaleInProgress
                        ? "This job is active but past its deadline or missing one. Cancelling will unlock the contract and notify the assigned agent."
                        : "This will cancel the job operation. This action cannot be undone."}
                      {isFunded &&
                        " Since this job is funded, the escrowed USDC will be automatically refunded to your wallet on-chain."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Job</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleAction("cancel")}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isFunded ? "Cancel & Refund" : "Cancel Job"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {(job.status === "IN_PROGRESS" || job.status === "IN_REVIEW") &&
              isPoster && (
                <Button
                  variant="outline"
                  className="border-red-500/20 hover:bg-red-500/10 hover:text-red-500 w-full sm:w-auto"
                  onClick={handleDispute}
                >
                  Open Dispute
                </Button>
              )}

            {!isPoster && lastRejectedEvent && (
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                onClick={() => {
                  const prev = job.outputData as Record<string, unknown> | null;
                  if (prev && Object.keys(prev).length > 0) {
                    setResubmitFields(
                      Object.fromEntries(
                        Object.keys(prev).map((k) => [
                          k,
                          String(prev[k] ?? ""),
                        ]),
                      ),
                    );
                  }
                  setIsResubmitOpen(true);
                }}
              >
                <Send className="mr-2 h-4 w-4" /> Resubmit Work
              </Button>
            )}

            {job.agent && (
              <Button variant="outline" asChild className="w-full sm:w-auto">
                <Link
                  to={`/dashboard/messages?user=${isPoster ? job.agent.ownerId : job.posterId}&agent=${job.agent.id}`}
                >
                  <MessageSquare className="mr-2 h-4 w-4" /> Chat with{" "}
                  {isPoster ? "Agent Team" : "Client"}
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Rejection Banner */}
        {lastRejectedEvent && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex flex-col md:flex-row gap-4 items-start">
            <div className="h-9 w-9 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
              <ThumbsDown className="h-4 w-4 text-red-400" />
            </div>
            <div className="flex-1 space-y-1">
              <p className="font-bold text-red-400 text-sm">
                Work Rejected —{" "}
                {formatDistanceToNow(new Date(lastRejectedEvent.createdAt))} ago
              </p>
              {lastRejectedEvent.payload?.category && (
                <p className="text-xs text-muted-foreground">
                  Category:{" "}
                  <span className="font-medium text-red-300">
                    {lastRejectedEvent.payload.category}
                  </span>
                </p>
              )}
              {lastRejectedEvent.payload?.reason && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  "{lastRejectedEvent.payload.reason}"
                </p>
              )}
              {isPoster ? (
                <p className="text-xs text-muted-foreground">
                  The agent has been notified and can resubmit.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Please address the feedback above and resubmit your work.
                </p>
              )}
            </div>
          </div>
        )}

        {/* The bid decision, above the fold: is the money there, who else is
            bidding and at what price, and what counts as done. */}
        <JobFactsPanel
          job={job}
          bids={bids}
          isPoster={isPoster}
          myAgentIds={(myAgents ?? []).map((a: { id: string }) => a.id)}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content (Tabs) */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue={initialTab} className="w-full">
              <TabsList className="w-full bg-muted/50 p-1 h-auto flex overflow-x-auto gap-0.5">
                <TabsTrigger
                  value="details"
                  className="flex-shrink-0 text-xs sm:text-sm whitespace-nowrap"
                >
                  Details
                </TabsTrigger>
                {isPoster && (
                  <TabsTrigger
                    value="bids"
                    className="flex-shrink-0 text-xs sm:text-sm whitespace-nowrap"
                  >
                    Bids {(bids?.length || 0) > 0 && `(${bids?.length})`}
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="reports"
                  className="flex-shrink-0 text-xs sm:text-sm whitespace-nowrap"
                >
                  Live Reports
                </TabsTrigger>
                <TabsTrigger
                  value="files"
                  className="flex-shrink-0 text-xs sm:text-sm whitespace-nowrap"
                >
                  Work Output
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="mt-4 space-y-6">
                <DetailsTab job={job} />
                <JobWorkNetwork jobId={job.id} isPoster={isPoster} isContractor={!!job.agentId && (myAgents ?? []).some((agent: { id: string }) => agent.id === job.agentId)} />
                {isPoster && job.status === "OPEN" && !job.forumRewardSlot && searchParams.get("agent") && <JobHireReview key={job.id + ":" + searchParams.get("agent")} jobId={job.id} title={job.title} budget={job.budgetUsdc} token={job.tokenSymbol ?? "USDC"} agentId={searchParams.get("agent")!} />}
                {isPoster && job.status === "OPEN" && !job.forumRewardSlot && <JobCandidates key={job.id} jobId={job.id} requiredSkills={job.requiredSkills} preferredSkills={job.preferredSkills} />}
              </TabsContent>

              {isPoster && (
                <TabsContent value="bids" className="mt-4">
                  <BidsTab
                    job={job}
                    bids={bids}
                    isAssignOpen={isAssignOpen}
                    setIsAssignOpen={setIsAssignOpen}
                    pendingAgentId={pendingAgentId}
                    setPendingAgentId={setPendingAgentId}
                    handleAcceptBid={handleAcceptBid}
                  />
                </TabsContent>
              )}

              <TabsContent value="reports" className="mt-4">
                <ReportsTab jobId={job.id} />
              </TabsContent>

              <TabsContent value="files" className="mt-4">
                <div className="space-y-4">
                  <AcceptanceCriteriaCard criteria={job.acceptanceCriteria} />

                  {/* Inline rejection banner — surfaces why without revealing output */}
                  {lastRejectedEvent && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex gap-3 items-start">
                      <ThumbsDown className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <p className="font-bold text-red-400 text-sm">
                          Last submission was rejected —{" "}
                          {formatDistanceToNow(
                            new Date(lastRejectedEvent.createdAt),
                          )}{" "}
                          ago
                        </p>
                        {lastRejectedEvent.payload?.category && (
                          <p className="text-xs text-muted-foreground">
                            Category:{" "}
                            <span className="font-medium text-red-300">
                              {lastRejectedEvent.payload.category}
                            </span>
                          </p>
                        )}
                        {lastRejectedEvent.payload?.reason && (
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            "{lastRejectedEvent.payload.reason}"
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {(() => {
                    const hasBeenSubmitted =
                      job.status === "IN_REVIEW" || job.status === "COMPLETED";
                    const submittedEvent = events?.find(
                      (e) => e.type === "SUBMITTED",
                    );

                    // Nothing submitted yet
                    if (!hasBeenSubmitted && !job.outputData) {
                      return (
                        <Card className="glass-panel">
                          <CardContent className="py-16">
                            <div className="flex flex-col items-center justify-center text-center">
                              <Paperclip className="h-10 w-10 text-muted-foreground mb-4 opacity-20" />
                              <h3 className="font-bold mb-1">
                                No Output Submitted
                              </h3>
                              <p className="text-sm text-muted-foreground max-w-sm">
                                The agent hasn't submitted any work output yet.
                                Output will appear here once submitted.
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Redacted for non-poster
                    if (!job.outputData && hasBeenSubmitted) {
                      return (
                        <Card className="glass-panel">
                          <CardContent className="py-12">
                            <div className="flex flex-col items-center justify-center text-center">
                              <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                                <EyeOff className="h-6 w-6 text-amber-400" />
                              </div>
                              <h3 className="font-bold text-amber-400 mb-2">
                                Output Obscured
                              </h3>
                              <p className="text-sm text-muted-foreground max-w-md">
                                Work has been submitted. Output is only visible
                                to the job poster and the agent owner.
                              </p>
                              <Badge
                                variant="outline"
                                className="mt-4 border-amber-500/30 text-amber-400"
                              >
                                Privacy Protected
                              </Badge>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Poster/agent owner can see the output
                    if (job.outputData) {
                      return (
                        <>
                          {/* Submission Info Banner */}
                          <Card className="glass-panel border-l-4 border-l-purple-500/50">
                            <CardContent className="py-4">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                  <div className="h-9 w-9 rounded-full bg-purple-500/10 flex items-center justify-center">
                                    <Bot className="h-4 w-4 text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold">
                                      {job.agent ? (
                                        <Link
                                          to={`/agents/${job.agent.id}`}
                                          className="hover:text-primary hover:underline"
                                          title={`View @${job.agent.id}'s profile`}
                                        >
                                          @{job.agent.id}
                                        </Link>
                                      ) : (
                                        "Agent"
                                      )}{" "}
                                      submitted work
                                    </p>
                                    <p className="text-[10px] text-muted-foreground font-mono">
                                      {submittedEvent
                                        ? formatDistanceToNow(
                                            new Date(submittedEvent.createdAt),
                                          ) + " ago"
                                        : "Submitted"}
                                    </p>
                                  </div>
                                </div>
                                <Badge
                                  className={
                                    job.status === "COMPLETED"
                                      ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                      : "bg-purple-500/20 text-purple-400 border-purple-500/30"
                                  }
                                >
                                  {job.status === "COMPLETED"
                                    ? "Approved"
                                    : "Pending Review"}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Output Card with Reveal Gate */}
                          <Card className="glass-panel">
                            <CardHeader className="pb-2">
                              <CardTitle className="flex items-center gap-2">
                                <Paperclip className="h-4 w-4" /> Work Output
                              </CardTitle>
                              <CardDescription>
                                {isPoster && job.status === "IN_REVIEW"
                                  ? "Review the output carefully before approving or rejecting."
                                  : "Results submitted by the agent."}
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              {isOutputRevealed ||
                              job.status === "COMPLETED" ? (
                                <div className="space-y-4">
                                  <SchemaDisplay
                                    schema={job.template?.outputSchema}
                                    data={job.outputData}
                                    title="Submitted Output"
                                  />
                                  {isOutputRevealed &&
                                    job.status !== "COMPLETED" && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground"
                                        onClick={() =>
                                          setIsOutputRevealed(false)
                                        }
                                      >
                                        <EyeOff className="mr-2 h-3 w-3" /> Hide
                                        Output
                                      </Button>
                                    )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-10 text-center border-2 border-dashed border-divider rounded-xl bg-surface-subtle/60">
                                  <div className="molt-icon-surface h-14 w-14 rounded-full flex items-center justify-center mb-4">
                                    <Eye className="h-6 w-6 text-primary" />
                                  </div>
                                  <h3 className="font-bold mb-1">
                                    Output Ready for Review
                                  </h3>
                                  <p className="text-sm text-muted-foreground max-w-sm mb-5">
                                    Click below to reveal the agent's submitted
                                    work output.
                                  </p>
                                  <Button
                                    onClick={() => setIsOutputRevealed(true)}
                                  >
                                    <Eye className="mr-2 h-4 w-4" /> Reveal
                                    Output
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </Card>

                          {/* Review Actions — only shown after reveal, only for poster in IN_REVIEW */}
                          {isPoster &&
                            job.status === "IN_REVIEW" &&
                            isOutputRevealed && (
                              <Card className="glass-panel border-t-4 border-t-primary/30">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                    <ShieldAlert className="h-4 w-4" /> Review
                                    Actions
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <p className="text-sm text-muted-foreground mb-4">
                                    After reviewing the output, choose to
                                    approve and release payment, or reject with
                                    feedback so the agent can try again.
                                  </p>
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <Button
                                      variant="outline"
                                      className="border-red-500/20 hover:bg-red-500/10 hover:text-red-400 flex-1"
                                      onClick={() => setIsRejectOpen(true)}
                                    >
                                      <ThumbsDown className="mr-2 h-4 w-4" />{" "}
                                      Reject with Feedback
                                    </Button>
                                    <Button
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                                      onClick={() =>
                                        setIsApproveConfirmOpen(true)
                                      }
                                    >
                                      <ThumbsUp className="mr-2 h-4 w-4" />{" "}
                                      Approve & Release Payment
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                        </>
                      );
                    }

                    return null;
                  })()}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6 pb-24 md:pb-0">
            {/* Who posted this, and whether they have ever paid an agent —
                the first thing to know before spending a bid. */}
            <PosterCard
              stats={(job as any)?.posterStats}
              isPoster={isPoster}
            />

            <Card className="glass-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Operation Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 relative pl-2 pt-2">
                  <div className="absolute left-[11px] top-4 bottom-4 w-[2px] bg-muted" />
                  {timeline.map((row) => (
                    <div
                      key={row.id}
                      className="flex gap-3 relative items-start"
                    >
                      <div className="h-6 w-6 rounded-full bg-background border-2 border-primary z-10 flex items-center justify-center shrink-0 mt-0.5">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      </div>
                      <div className="pb-4">
                        <p className="text-sm font-medium leading-tight">
                          {row.type.replace(/_/g, " ")}
                          {row.count > 1 && (
                            <span className="ml-1.5 text-xs font-mono text-primary">
                              &times;{row.count}
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {row.count > 1
                            ? `${formatDistanceToNow(new Date(row.lastAt))} ago – ${formatDistanceToNow(new Date(row.firstAt))} ago`
                            : `${formatDistanceToNow(new Date(row.firstAt))} ago`}{" "}
                          by {row.actorType}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!events || events.length === 0) && (
                    <p className="text-xs text-muted-foreground italic">
                      No events recorded yet.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Smart Contract
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <EscrowGuaranteeBadge
                  status={
                    isFunded
                      ? job.status === "COMPLETED"
                        ? "RELEASED"
                        : "LOCKED"
                      : "UNFUNDED"
                  }
                  paymentProvider={job.paymentProvider}
                  className="mb-3"
                />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Escrow Balance</span>
                  <span
                    className={`font-mono font-bold ${isFunded ? "text-emerald-400" : "text-muted-foreground"}`}
                  >
                    {isFunded ? `$${job.budgetUsdc}` : "$0"} USDC
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Protocol Status</span>
                  <Badge
                    variant="outline"
                    className={`text-[10px] h-5 ${isFunded ? (job.status === "COMPLETED" ? "border-emerald-500/50 text-emerald-500" : "border-blue-500/50 text-blue-500") : "border-divider text-muted-foreground"}`}
                  >
                    {isFunded
                      ? job.status === "COMPLETED"
                        ? "RELEASED"
                        : "LOCKED"
                      : "UNFUNDED"}
                  </Badge>
                </div>
                {needsRetryRelease && (
                  <div className="pt-2 border-t border-amber-500/20">
                    <p className="text-[10px] text-amber-400/80 mb-2">
                      On-chain release may not have completed.
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-7 text-[11px] border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      onClick={handleRetryRelease}
                    >
                      <RefreshCw className="mr-1.5 h-3 w-3" />
                      Retry Escrow Release
                    </Button>
                  </div>
                )}
                {job.paymentProvider === "STRIPE"
                  ? (job.stripeChargeId || isFunded) && (
                      <div className="pt-1 border-t border-divider/60">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                          Card hold (Stripe)
                        </p>
                        {job.stripeChargeId ? (
                          <span className="text-[10px] font-mono text-primary/70 break-all">
                            {job.stripeChargeId}
                          </span>
                        ) : (
                          <span className="text-[10px] font-mono text-muted-foreground">
                            Authorized — held until release
                          </span>
                        )}
                      </div>
                    )
                  : job.escrowTxHash && (
                      <div className="pt-1 border-t border-divider/60">
                        <p className="text-[10px] text-muted-foreground uppercase font-bold mb-1">
                          Deposit Tx
                        </p>
                        <a
                          href={`https://basescan.org/tx/${job.escrowTxHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] font-mono text-primary/70 hover:text-primary break-all"
                        >
                          {job.escrowTxHash.slice(0, 20)}...
                          {job.escrowTxHash.slice(-8)}
                        </a>
                      </div>
                    )}
                {releaseTxHash && (
                  <div className="pt-1 border-t border-divider/60">
                    <p className="text-[10px] text-emerald-500/70 uppercase font-bold mb-1">
                      Release Tx
                    </p>
                    <a
                      href={`https://basescan.org/tx/${releaseTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-mono text-emerald-400/70 hover:text-emerald-400 break-all"
                    >
                      {releaseTxHash.slice(0, 20)}...{releaseTxHash.slice(-8)}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Rejection Dialog */}
      <AlertDialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ThumbsDown className="h-5 w-5 text-red-400" />
              Reject Submitted Work
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              Rejecting will move the job back to <strong>IN_PROGRESS</strong>{" "}
              so the agent can address the issues and resubmit. Your feedback
              will be sent to the agent via webhook and email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                onValueChange={setRejectionCategory}
                value={rejectionCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Incomplete Work">
                    Incomplete Work
                  </SelectItem>
                  <SelectItem value="Wrong Output Format">
                    Wrong Output Format
                  </SelectItem>
                  <SelectItem value="Quality Below Standard">
                    Quality Below Standard
                  </SelectItem>
                  <SelectItem value="Not What Was Requested">
                    Not What Was Requested
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Detailed Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                placeholder="Explain what needs to be fixed or improved (min 20 characters)..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px] resize-none"
              />
              <p className="text-[10px] text-muted-foreground">
                {rejectionReason.length}/20 characters minimum
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setRejectionReason("");
                setRejectionCategory("");
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                rejectionReason.length < 20 || transitionMutation.isPending
              }
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              onClick={async () => {
                try {
                  await transitionMutation.mutateAsync({
                    id: job.id,
                    action: "reject",
                    payload: {
                      reason: rejectionReason,
                      category: rejectionCategory || undefined,
                    },
                  });
                  toast.success("Work rejected. Feedback sent to agent.");
                  setRejectionReason("");
                  setRejectionCategory("");
                  setIsOutputRevealed(false);
                } catch (err: any) {
                  toast.error(
                    err.response?.data?.message || "Failed to reject",
                  );
                }
              }}
            >
              {transitionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rejecting...
                </>
              ) : (
                "Reject & Send Feedback"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve Confirmation Dialog */}
      <AlertDialog
        open={isApproveConfirmOpen}
        onOpenChange={setIsApproveConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ThumbsUp className="h-5 w-5 text-emerald-400" />
              Confirm Approval & Payment
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              You are about to approve the submitted work. This action will:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Payment Amount</span>
                <span className="font-bold text-emerald-400">
                  {job.budgetUsdc} USDC
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Agent</span>
                <span className="font-mono text-xs">
                  {job.agent ? `@${job.agent.id}` : "N/A"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Escrow</span>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${isFunded ? "border-blue-500/50 text-blue-500" : "border-divider text-muted-foreground"}`}
                >
                  {isFunded ? "FUNDED — Will auto-release" : "UNFUNDED"}
                </Badge>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {isFunded
                ? "The escrowed USDC will be automatically released to the agent's wallet on-chain."
                : "No escrow is funded for this job. The job will be marked as completed."}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={transitionMutation.isPending}
              onClick={async () => {
                try {
                  await handleApprove();
                  setIsOutputRevealed(false);
                } catch (err: any) {
                  // handleApprove already shows toast
                }
              }}
            >
              {transitionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" /> Confirm & Release
                  Payment
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resubmit Work Dialog */}
      {(() => {
        const outputSchema = job.template?.outputSchema as any;
        const schemaProps: Record<string, any> = outputSchema?.properties || {};
        const required: string[] = outputSchema?.required || [];

        // Field keys: from previous output data (pre-populated) → schema → generic
        const prevOutput = job.outputData as Record<string, unknown> | null;
        const fieldKeys: string[] =
          Object.keys(resubmitFields).length > 0
            ? Object.keys(resubmitFields)
            : prevOutput && Object.keys(prevOutput).length > 0
              ? Object.keys(prevOutput)
              : Object.keys(schemaProps).length > 0
                ? Object.keys(schemaProps)
                : ["result"];

        const allRequiredFilled =
          fieldKeys
            .filter((k) => required.includes(k) || required.length === 0)
            .every((k) => resubmitFields[k]?.trim()) &&
          fieldKeys.some((k) => resubmitFields[k]?.trim());

        return (
          <Dialog
            open={isResubmitOpen}
            onOpenChange={(open) => {
              setIsResubmitOpen(open);
              if (!open) setResubmitFields({});
            }}
          >
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-amber-400" />
                  Resubmit Work
                </DialogTitle>
                <DialogDescription className="text-sm leading-relaxed">
                  Address the client's feedback and fill in the required output
                  fields.
                </DialogDescription>
              </DialogHeader>
              {lastRejectedEvent && (
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10 text-sm">
                  <p className="text-[10px] font-bold uppercase text-red-400 mb-1">
                    Rejection Feedback
                  </p>
                  {lastRejectedEvent.payload?.category && (
                    <p className="text-xs text-muted-foreground mb-1">
                      Category:{" "}
                      <span className="font-medium text-red-300">
                        {lastRejectedEvent.payload.category}
                      </span>
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {lastRejectedEvent.payload?.reason || "No reason provided."}
                  </p>
                </div>
              )}
              <div className="space-y-4">
                {fieldKeys.map((key) => (
                  <div key={key} className="space-y-2">
                    <Label>
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (s) => s.toUpperCase())}
                      {required.includes(key) && (
                        <span className="text-destructive ml-1">*</span>
                      )}
                    </Label>
                    <Textarea
                      placeholder={
                        schemaProps[key]?.description || `Enter ${key}...`
                      }
                      value={resubmitFields[key] || ""}
                      onChange={(e) =>
                        setResubmitFields((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      className="min-h-[100px] resize-none"
                    />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsResubmitOpen(false);
                    setResubmitFields({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={!allRequiredFilled || transitionMutation.isPending}
                  onClick={async () => {
                    const outputData: Record<string, unknown> = {};
                    for (const key of fieldKeys) {
                      const val = resubmitFields[key] || "";
                      const prevVal = prevOutput?.[key];
                      const isNumeric =
                        schemaProps[key]?.type === "number" ||
                        schemaProps[key]?.type === "integer" ||
                        typeof prevVal === "number";
                      outputData[key] = isNumeric ? Number(val) : val;
                    }
                    try {
                      await transitionMutation.mutateAsync({
                        id: job.id,
                        action: "submit",
                        payload: { outputData },
                      });
                      trackMarketingEvent("work_submitted", { job_id: job.id });
                      toast.success("Work resubmitted for review!");
                      setIsResubmitOpen(false);
                      setResubmitFields({});
                    } catch (err: any) {
                      toast.error(
                        err.response?.data?.message || "Failed to resubmit",
                      );
                    }
                  }}
                >
                  {transitionMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" /> Submit for Review
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Tx Error Dialog */}
      <AlertDialog
        open={!!txError}
        onOpenChange={(open) => {
          if (!open) setTxError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {txError?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              {txError?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setTxError(null)}>
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phones only: the brief runs several screens, so the header's Apply
          button scrolls out of reach. Pin it, with the escrow state alongside. */}
      <MobileJobBar
        funded={Boolean(isFunded)}
        budgetUsdc={job.budgetUsdc}
        status={job.status}
        isPoster={isPoster}
        hasBid={Boolean(
          bids?.some(
            (b: any) =>
              b.status === "PENDING" &&
              (myAgents ?? []).some((a: { id: string }) => a.id === b.agent?.id),
          ),
        )}
        onApply={() => setIsApplyOpen(true)}
      />
    </>
  );
}

function JobDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <Skeleton className="h-20 w-full" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Skeleton className="md:col-span-2 h-[500px]" />
        <Skeleton className="h-[500px]" />
      </div>
    </div>
  );
}
